import { PromptTemplate, ChainOfThoughtTemplate } from '../types/prompt';

export const multiModelTemplates: PromptTemplate[] = [
  // Claude Templates
  {
    id: 'claude-analysis',
    name: 'Claude Deep Analysis',
    category: 'analysis',
    platform: 'claude',
    template: `Analyze {topic} with the following approach:

1. Context and Background:
{context}

2. Key Points to Address:
{key_points}

3. Analysis Depth: {depth_level}

Please provide:
- Comprehensive overview
- Critical insights
- Potential implications
- Actionable recommendations

Format: {output_format}`,
    variables: ['topic', 'context', 'key_points', 'depth_level', 'output_format'],
    examples: [
      'Analyze market trends in renewable energy',
      'Deep dive into machine learning algorithms'
    ],
    tips: [
      'Claude excels at nuanced, detailed analysis',
      'Provide clear context for best results'
    ],
    costPerUse: 0.024,
    modelVersion: 'claude-3-opus'
  },
  {
    id: 'claude-creative-writing',
    name: 'Claude Creative Writing',
    category: 'creative',
    platform: 'claude',
    template: `Write a {genre} story with these elements:

Setting: {setting}
Main Character: {character}
Conflict: {conflict}
Tone: {tone}
Length: {length} words

Style Guidelines:
- Writing style: {style}
- Point of view: {pov}
- Special requirements: {requirements}`,
    variables: ['genre', 'setting', 'character', 'conflict', 'tone', 'length', 'style', 'pov', 'requirements'],
    costPerUse: 0.024,
    modelVersion: 'claude-3-opus'
  },
  {
    id: 'claude-code-review',
    name: 'Claude Code Review',
    category: 'code',
    platform: 'claude',
    template: `Review this {language} code:

\`\`\`{language}
{code}
\`\`\`

Focus Areas:
- {focus_areas}

Please analyze:
1. Code quality and best practices
2. Potential bugs or issues
3. Performance considerations
4. Security vulnerabilities
5. Suggested improvements

Review depth: {review_depth}`,
    variables: ['language', 'code', 'focus_areas', 'review_depth'],
    costPerUse: 0.024,
    modelVersion: 'claude-3-opus'
  },

  // GPT-4 Templates
  {
    id: 'gpt4-reasoning',
    name: 'GPT-4 Complex Reasoning',
    category: 'analysis',
    platform: 'gpt4',
    template: `Problem: {problem_statement}

Context: {context}

Constraints: {constraints}

Please solve this step-by-step:
1. Understand the problem
2. Identify key components
3. Develop solution approach
4. Implement solution
5. Verify and optimize

Output format: {format}`,
    variables: ['problem_statement', 'context', 'constraints', 'format'],
    costPerUse: 0.03,
    modelVersion: 'gpt-4-turbo'
  },
  {
    id: 'gpt4-data-analysis',
    name: 'GPT-4 Data Analysis',
    category: 'analysis',
    platform: 'gpt4',
    template: `Analyze this dataset:

Data Description: {data_description}

Metrics: {metrics}

Analysis Goals:
{goals}

Provide:
- Statistical summary
- Key patterns and trends
- Anomalies or outliers
- Visualizations recommendations
- Business insights

Format: {output_format}`,
    variables: ['data_description', 'metrics', 'goals', 'output_format'],
    costPerUse: 0.03,
    modelVersion: 'gpt-4-turbo'
  },

  // GPT-4 Vision Templates
  {
    id: 'gpt4v-image-analysis',
    name: 'GPT-4V Image Analysis',
    category: 'analysis',
    platform: 'gpt4-vision',
    template: `[Image: {image_url}]

Analyze this image for:
- {analysis_focus}

Detail level: {detail_level}

Please provide:
1. Visual description
2. Key elements identification
3. Technical analysis
4. Contextual interpretation
5. {custom_requirements}`,
    variables: ['image_url', 'analysis_focus', 'detail_level', 'custom_requirements'],
    costPerUse: 0.04,
    modelVersion: 'gpt-4-vision'
  },
  {
    id: 'gpt4v-ui-review',
    name: 'GPT-4V UI/UX Review',
    category: 'analysis',
    platform: 'gpt4-vision',
    template: `[Screenshot: {screenshot_url}]

Review this UI design for:

Target Users: {target_users}
Platform: {platform_type}

Evaluate:
- Visual hierarchy
- Color scheme and contrast
- Typography
- Layout and spacing
- Accessibility
- User flow
- {additional_criteria}

Provide improvement suggestions.`,
    variables: ['screenshot_url', 'target_users', 'platform_type', 'additional_criteria'],
    costPerUse: 0.04,
    modelVersion: 'gpt-4-vision'
  },

  // Gemini Templates
  {
    id: 'gemini-multimodal',
    name: 'Gemini Multimodal Analysis',
    category: 'analysis',
    platform: 'gemini',
    template: `Analyze these inputs:

Text: {text_input}
[Image: {image_input}]
[Video: {video_input}]

Task: {task_description}

Provide:
- Comprehensive analysis
- Cross-modal insights
- Relationships between inputs
- {specific_requirements}

Output style: {output_style}`,
    variables: ['text_input', 'image_input', 'video_input', 'task_description', 'specific_requirements', 'output_style'],
    costPerUse: 0.025,
    modelVersion: 'gemini-pro'
  },
  {
    id: 'gemini-research',
    name: 'Gemini Research Assistant',
    category: 'analysis',
    platform: 'gemini',
    template: `Research Topic: {topic}

Research Scope:
- Field: {field}
- Depth: {depth}
- Time Period: {time_period}

Deliverables:
1. Literature review
2. Key findings summary
3. Methodology analysis
4. Future directions
5. {additional_requirements}

Citation style: {citation_style}`,
    variables: ['topic', 'field', 'depth', 'time_period', 'additional_requirements', 'citation_style'],
    costPerUse: 0.025,
    modelVersion: 'gemini-pro'
  },

  // DALL-E 3 Templates
  {
    id: 'dalle3-photorealistic',
    name: 'DALL-E 3 Photorealistic',
    category: 'image',
    platform: 'dalle3',
    template: `{subject}, photorealistic, {lighting} lighting, {composition} composition, shot with {camera}, {lens}mm lens, {settings}, {mood} mood, {environment}, high detail, 8K resolution`,
    variables: ['subject', 'lighting', 'composition', 'camera', 'lens', 'settings', 'mood', 'environment'],
    examples: [
      'Portrait with natural lighting',
      'Product photography with studio setup'
    ],
    costPerUse: 0.04,
    modelVersion: 'dall-e-3'
  },
  {
    id: 'dalle3-artistic',
    name: 'DALL-E 3 Artistic Style',
    category: 'image',
    platform: 'dalle3',
    template: `{subject} in the style of {artist_style}, {color_palette} colors, {technique} technique, {composition}, {mood} atmosphere, {medium}, {details}`,
    variables: ['subject', 'artist_style', 'color_palette', 'technique', 'composition', 'mood', 'medium', 'details'],
    examples: [
      'Landscape in impressionist style',
      'Portrait in oil painting technique'
    ],
    costPerUse: 0.04,
    modelVersion: 'dall-e-3'
  },
  {
    id: 'dalle3-concept-art',
    name: 'DALL-E 3 Concept Art',
    category: 'image',
    platform: 'dalle3',
    template: `Concept art of {concept}, {style} style, {view} view, {environment} environment, {lighting}, {color_scheme} color scheme, professional digital art, detailed design, {additional_details}`,
    variables: ['concept', 'style', 'view', 'environment', 'lighting', 'color_scheme', 'additional_details'],
    costPerUse: 0.04,
    modelVersion: 'dall-e-3'
  },

  // Stable Diffusion XL Templates
  {
    id: 'sdxl-cinematic',
    name: 'SDXL Cinematic',
    category: 'image',
    platform: 'stable-diffusion-xl',
    template: `{subject}, cinematic shot, {shot_type}, {lighting} lighting, {color_grading} color grading, film grain, {lens_type} lens, bokeh, anamorphic, {atmosphere}, {film_style} style`,
    variables: ['subject', 'shot_type', 'lighting', 'color_grading', 'lens_type', 'atmosphere', 'film_style'],
    parameters: {
      negative_prompt: 'low quality, blurry, distorted',
      steps: 30,
      cfg_scale: 7
    },
    costPerUse: 0.002,
    modelVersion: 'sdxl-1.0'
  },
  {
    id: 'sdxl-anime',
    name: 'SDXL Anime Style',
    category: 'image',
    platform: 'stable-diffusion-xl',
    template: `{character}, anime style, {anime_subgenre}, {hair_color} hair, {eye_color} eyes, {outfit}, {pose}, {background}, {lighting}, detailed illustration, high quality`,
    variables: ['character', 'anime_subgenre', 'hair_color', 'eye_color', 'outfit', 'pose', 'background', 'lighting'],
    parameters: {
      negative_prompt: 'realistic, photographic, low quality',
      steps: 25,
      cfg_scale: 7.5
    },
    costPerUse: 0.002,
    modelVersion: 'sdxl-1.0'
  },
  {
    id: 'sdxl-product',
    name: 'SDXL Product Design',
    category: 'image',
    platform: 'stable-diffusion-xl',
    template: `{product} design, {material} material, {style} style, {color_scheme} colors, product photography, studio lighting, {background} background, clean composition, professional render, {details}`,
    variables: ['product', 'material', 'style', 'color_scheme', 'background', 'details'],
    parameters: {
      negative_prompt: 'low quality, amateur, cluttered',
      steps: 35,
      cfg_scale: 8
    },
    costPerUse: 0.002,
    modelVersion: 'sdxl-1.0'
  },

  // LLaMA Templates
  {
    id: 'llama-instruction',
    name: 'LLaMA Instruction Following',
    category: 'text',
    platform: 'llama',
    template: `### Instruction:
{instruction}

### Context:
{context}

### Requirements:
{requirements}

### Response:`,
    variables: ['instruction', 'context', 'requirements'],
    costPerUse: 0.001,
    modelVersion: 'llama-2-70b'
  },
  {
    id: 'llama-chat',
    name: 'LLaMA Conversational',
    category: 'text',
    platform: 'llama',
    template: `[INST] <<SYS>>
You are a helpful assistant with expertise in {expertise}.
Personality: {personality}
<</SYS>>

{user_message} [/INST]`,
    variables: ['expertise', 'personality', 'user_message'],
    costPerUse: 0.001,
    modelVersion: 'llama-2-70b-chat'
  },

  // Mistral Templates
  {
    id: 'mistral-coding',
    name: 'Mistral Code Generation',
    category: 'code',
    platform: 'mistral',
    template: `<s>[INST] Generate {language} code for:

Task: {task_description}
Requirements: {requirements}
Constraints: {constraints}

Include:
- Comments explaining the logic
- Error handling
- {additional_features}
[/INST]`,
    variables: ['language', 'task_description', 'requirements', 'constraints', 'additional_features'],
    costPerUse: 0.0006,
    modelVersion: 'mistral-medium'
  },
  {
    id: 'mistral-summary',
    name: 'Mistral Summarization',
    category: 'text',
    platform: 'mistral',
    template: `<s>[INST] Summarize the following {content_type}:

{content}

Summary requirements:
- Length: {length}
- Style: {style}
- Focus: {focus_areas}
- Format: {format}
[/INST]`,
    variables: ['content_type', 'content', 'length', 'style', 'focus_areas', 'format'],
    costPerUse: 0.0006,
    modelVersion: 'mistral-medium'
  }
];

export const chainOfThoughtTemplates: ChainOfThoughtTemplate[] = [
  {
    id: 'cot-problem-solving',
    name: 'Structured Problem Solving',
    description: 'Break down complex problems into manageable steps',
    steps: [
      {
        id: 'understand',
        instruction: 'First, let me understand the problem:',
        example: 'The problem asks for X, given constraints Y and Z',
        validation: 'Have I correctly identified all requirements?'
      },
      {
        id: 'decompose',
        instruction: 'Breaking this down into sub-problems:',
        example: '1. Sub-problem A\n2. Sub-problem B\n3. Sub-problem C',
        validation: 'Are these sub-problems independent and complete?'
      },
      {
        id: 'solve',
        instruction: 'Solving each sub-problem:',
        example: 'For sub-problem A: [detailed solution]',
        validation: 'Is each solution correct and efficient?'
      },
      {
        id: 'integrate',
        instruction: 'Combining the solutions:',
        example: 'Integrating solutions A, B, and C gives us...',
        validation: 'Does the combined solution address the original problem?'
      },
      {
        id: 'verify',
        instruction: 'Verification:',
        example: 'Testing with example inputs...',
        validation: 'Have I checked edge cases and confirmed correctness?'
      }
    ],
    finalPromptTemplate: '{all_steps}\n\nFinal Answer: {solution}'
  },
  {
    id: 'cot-analysis',
    name: 'Analytical Reasoning',
    description: 'Systematic analysis with evidence-based reasoning',
    steps: [
      {
        id: 'observe',
        instruction: 'Observations:',
        example: 'I notice that...',
        validation: 'Are these observations objective and relevant?'
      },
      {
        id: 'hypothesize',
        instruction: 'Forming hypotheses:',
        example: 'Based on observations, possible explanations are...',
        validation: 'Are these hypotheses testable?'
      },
      {
        id: 'evidence',
        instruction: 'Evaluating evidence:',
        example: 'Evidence for/against each hypothesis...',
        validation: 'Have I considered all available evidence?'
      },
      {
        id: 'conclude',
        instruction: 'Drawing conclusions:',
        example: 'The most supported conclusion is...',
        validation: 'Is this conclusion logically sound?'
      }
    ],
    finalPromptTemplate: '{all_steps}\n\nConclusion: {conclusion}\nConfidence: {confidence_level}'
  },
  {
    id: 'cot-creative',
    name: 'Creative Ideation',
    description: 'Structured approach to creative thinking',
    steps: [
      {
        id: 'explore',
        instruction: 'Exploring the creative space:',
        example: 'Related concepts, themes, and inspirations...',
        validation: 'Have I explored broadly enough?'
      },
      {
        id: 'generate',
        instruction: 'Generating ideas:',
        example: 'Idea 1: ...\nIdea 2: ...\nIdea 3: ...',
        validation: 'Are these ideas diverse and original?'
      },
      {
        id: 'combine',
        instruction: 'Combining and refining:',
        example: 'Merging ideas 1 and 3 creates...',
        validation: 'Does this create something novel?'
      },
      {
        id: 'polish',
        instruction: 'Polishing the concept:',
        example: 'Final refined concept with details...',
        validation: 'Is this ready for implementation?'
      }
    ],
    finalPromptTemplate: '{all_steps}\n\nFinal Creative Output: {output}'
  }
];

export const modelCapabilities: Record<string, any> = {
  'midjourney': {
    strengths: ['Artistic quality', 'Aesthetic styles', 'Creative interpretation', 'Photo-realistic'],
    costPerImage: 0.02,
    bestFor: ['Artistic images', 'Creative concepts', 'Marketing visuals']
  },
  'google-imagefx': {
    strengths: ['Fast generation', 'Simple interface', 'Good quality', 'Free tier'],
    costPerImage: 0,
    bestFor: ['Quick prototypes', 'Simple images', 'Testing ideas']
  },
  'veo': {
    strengths: ['Video generation', 'Smooth motion', 'High quality', 'Long clips'],
    costPerVideo: 0.1,
    bestFor: ['Video content', 'Animations', 'Motion graphics']
  },
  'universal': {
    strengths: ['Works everywhere', 'Platform agnostic', 'Flexible', 'Adaptable'],
    costPerUse: 0,
    bestFor: ['Cross-platform use', 'General purpose', 'Testing']
  },
  'claude': {
    strengths: ['Deep analysis', 'Creative writing', 'Code understanding', 'Nuanced reasoning'],
    maxTokens: 100000,
    costPerToken: 0.000024,
    bestFor: ['Long-form content', 'Complex analysis', 'Technical documentation']
  },
  'gpt4': {
    strengths: ['General intelligence', 'Math reasoning', 'Code generation', 'Data analysis'],
    maxTokens: 128000,
    costPerToken: 0.00003,
    bestFor: ['Problem solving', 'Data analysis', 'API integration']
  },
  'gpt4-vision': {
    strengths: ['Image understanding', 'Visual analysis', 'OCR', 'UI/UX review'],
    maxTokens: 128000,
    costPerToken: 0.00004,
    bestFor: ['Image analysis', 'Design review', 'Visual QA']
  },
  'gemini': {
    strengths: ['Multimodal understanding', 'Research', 'Long context', 'Speed'],
    maxTokens: 1000000,
    costPerToken: 0.000025,
    bestFor: ['Research tasks', 'Document analysis', 'Multimodal projects']
  },
  'dalle3': {
    strengths: ['High quality images', 'Text rendering', 'Artistic styles', 'Photorealism'],
    costPerImage: 0.04,
    bestFor: ['Marketing visuals', 'Concept art', 'Illustrations']
  },
  'stable-diffusion-xl': {
    strengths: ['Fast generation', 'Customizable', 'Low cost', 'Fine-tuning support'],
    costPerImage: 0.002,
    bestFor: ['Bulk image generation', 'Prototyping', 'Custom styles']
  },
  'llama': {
    strengths: ['Open source', 'Customizable', 'Low cost', 'Privacy'],
    maxTokens: 4096,
    costPerToken: 0.000001,
    bestFor: ['Private deployment', 'Custom fine-tuning', 'Cost-sensitive apps']
  },
  'mistral': {
    strengths: ['Fast inference', 'Good for code', 'Efficient', 'European'],
    maxTokens: 32000,
    costPerToken: 0.0000006,
    bestFor: ['Code generation', 'European compliance', 'Real-time apps']
  }
};